import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';

import Sidebar from 'components/SignInSidebar';

import { signUpSocietyState } from 'lib/atoms/signUpSocietyAtom';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { fetchTranslationFiles, LOCALE_PAGES } from 'lib/services/page';

import styles from './Society.module.scss';
import { Step1, Step2, Step3, Step4, Step5 } from './Steps';
import UniversityAndSociety from './UniversityAndSociety';

const SignIn = (): ReactElement => {
  const router = useRouter();

  const [societyState] = useRecoilState(signUpSocietyState);
  const { society, step: storedStep } = societyState;

  const {
    isUnapprovedManager,
    manager,
    managerSociety,
    isManager,
    isAuthLoading,
    isInitialAuthLoading,
    isRecruiter,
    isUnapprovedRecruiter,
  } = useAuth();

  const flow_state = manager?.flow_state;

  const [step, setStep] = useState(storedStep);
  useEffect(() => {
    if (!manager) {
      setStep(storedStep);
    } else {
      if (manager?.user.data.attributes.is_confirmed) {
        if (!flow_state) {
          setStep(3);
        } else {
          if (flow_state === ONBOARDING_FLOW_STATE.PRE_COMPLETE) {
            setStep(6);
          } else if (flow_state === ONBOARDING_FLOW_STATE.PRE_4) {
            setStep(4);
          } else if (flow_state === ONBOARDING_FLOW_STATE.PRE_5) {
            setStep(5);
          }
        }
      } else {
        setStep(2);
      }
    }
  }, [manager]);

  const isNewSociety = useMemo(
    () =>
      managerSociety?.authenticated
        ? !(
            managerSociety?.icon_url &&
            managerSociety?.description &&
            managerSociety?.primary_topic
          )
        : true,
    [managerSociety]
  );

  const progressPercentage = useMemo(() => {
    const totalSteps = isNewSociety || society?.isNewSociety ? 6 : 4;
    return Math.floor((step / totalSteps) * 100);
  }, [managerSociety, isNewSociety, step]);

  if (isManager && !isUnapprovedManager) {
    if (flow_state === ONBOARDING_FLOW_STATE.COMPLETE) {
      router.push(PAGE_ROUTES.DASHBOARD);
    } else if (flow_state === ONBOARDING_FLOW_STATE.PRE_COMPLETE) {
      router.push(PAGE_ROUTES.SOCIETY_ONBOARDING);
    }
  }

  if (
    (flow_state === ONBOARDING_FLOW_STATE.PRE_COMPLETE &&
      isUnapprovedManager &&
      !isAuthLoading) ||
    step === 6
  ) {
    router.replace(PAGE_ROUTES.SIGN_UP_MANAGER_SUCCESS);
  }

  if (isRecruiter || isUnapprovedRecruiter) {
    router.replace(PAGE_ROUTES.RECRUITER_SIGN_UP);
  }

  if (isInitialAuthLoading) {
    return <></>;
  }

  return (
    <>
      <Head>
        <title>Society Sign Up | Huzzle</title>
        <meta name="description" content="Sign up to Huzzle as a Society." />
      </Head>
      <section className={styles.container} data-testid="Signin">
        {step === 0 && (
          <>
            <aside className={styles.sidebar}>
              <Sidebar />
            </aside>
            <main className={styles.main}>
              <UniversityAndSociety className={styles.form} setStep={setStep} />
            </main>
          </>
        )}
        {step === 1 && (
          <Step1 progress={progressPercentage} setStep={setStep} />
        )}
        {step === 2 && (
          <Step2 progress={progressPercentage} setStep={setStep} />
        )}
        {step === 3 && (
          <Step3 progress={progressPercentage} setStep={setStep} />
        )}
        {step === 4 && (
          <Step4 progress={progressPercentage} setStep={setStep} />
        )}
        {step === 5 && (
          <Step5 progress={progressPercentage} setStep={setStep} />
        )}
      </section>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.SIGNUP_MANAGER, LOCALE_PAGES.SIGN_IN],
      })),
    },
  };
}

export default SignIn;
